import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import useLeagueData from "../hooks/useLeagueData";
import useLeagueFixtures from "../hooks/useLeagueFixtures";
import { subscribeCompetitions } from "../services/competitionService";
import {
  DEFAULT_GALLERY_SLIDESHOW_INTERVAL,
  subscribeLeagueGallery,
} from "../services/leagueGalleryService";
import LiveTable from "../components/LiveTable";
import LeagueInsights from "../components/LeagueInsights";
import AwardsPanel from "../components/AwardsPanel";
import PromotionZone from "../components/PromotionZone";
import useStandingsMovement from "../hooks/useStandingsMovement";
import useLeagueVocabulary from "../hooks/useLeagueVocabulary";
import CompetitionDetailModal from "../components/CompetitionDetailModal";

const competitionEnd = (item) => {
  const date = item?.endDate || item?.startDate;
  if (!date) return null;
  const time = item?.endTime || "23:59:59";
  const value = new Date(`${date}T${time.length === 5 ? `${time}:00` : time}`);
  return Number.isNaN(value.getTime()) ? null : value;
};

function CompetitionCountdown({ item }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const end = competitionEnd(item);
  if (!end) return <span className="league-home-countdown">End TBC</span>;
  const remaining = Math.max(0, end.getTime() - now);
  if (remaining <= 0) return <span className="league-home-countdown ended">Finished</span>;
  const total = Math.floor(remaining / 1000);
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return (
    <span className="league-home-countdown">
      <b>{days}</b>d <b>{String(hours).padStart(2, "0")}</b>h{" "}
      <b>{String(minutes).padStart(2, "0")}</b>m{" "}
      <b>{String(seconds).padStart(2, "0")}</b>s
    </span>
  );
}

function ProgressiveLeagueImage({ image, motionEnabled }) {
  const [fullReady, setFullReady] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFullReady(false);
    setFailed(false);
  }, [image?.id, image?.heroUrl, image?.url]);

  const thumb = image?.thumbnailUrl || image?.mediumUrl || image?.heroUrl || image?.url || image?.originalUrl;
  const full = image?.heroUrl || image?.mediumUrl || image?.url || image?.originalUrl || image?.thumbnailUrl;

  return (
    <div className={`league-progressive-image${motionEnabled ? " ken-burns" : ""}${failed ? " image-failed" : ""}`}>
      <img
        className="league-progressive-thumb"
        src={thumb}
        alt={image?.caption || "Matrix League team"}
        loading="eager"
        decoding="async"
        fetchPriority="high"
        onError={() => setFailed(true)}
      />
      {!failed ? (
        <img
          className={`league-progressive-full${fullReady ? " ready" : ""}`}
          src={full}
          alt=""
          aria-hidden="true"
          loading="eager"
          decoding="async"
          onLoad={() => setFullReady(true)}
          onError={() => setFailed(true)}
        />
      ) : null}
      {!fullReady && !failed ? <span className="league-gallery-image-loader" aria-hidden="true" /> : null}
      {failed ? <div className="league-gallery-image-fallback">Image unavailable</div> : null}
    </div>
  );
}

function LeagueGallerySkeleton() {
  return (
    <div className="league-gallery-card league-gallery-skeleton" aria-label="Loading League photos" aria-busy="true">
      <div className="league-gallery-skeleton-shimmer" />
      <div className="league-gallery-skeleton-copy">
        <span />
        <strong />
        <small />
      </div>
    </div>
  );
}

function LeagueGallery({
  images,
  leader,
  vocabulary,
  intervalMs = DEFAULT_GALLERY_SLIDESHOW_INTERVAL,
  loading = false,
}) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [documentHidden, setDocumentHidden] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    setIndex((current) => (images.length ? Math.min(current, images.length - 1) : 0));
  }, [images.length]);

  useEffect(() => {
    const query = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(Boolean(query?.matches));
    update();
    query?.addEventListener?.("change", update);
    return () => query?.removeEventListener?.("change", update);
  }, []);

  useEffect(() => {
    const onVisibilityChange = () => setDocumentHidden(document.hidden);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  const autoPlaying = images.length > 1 && !paused && !documentHidden && !reducedMotion;
  useEffect(() => {
    if (!autoPlaying) return undefined;
    const id = setTimeout(() => setIndex((value) => (value + 1) % images.length), intervalMs);
    return () => clearTimeout(id);
  }, [autoPlaying, images.length, intervalMs, index]);

  const active = images[index] || images[0];
  const next = images.length > 1 ? images[(index + 1) % images.length] : null;

  useEffect(() => {
    if (!next) return;
    const thumb = new Image();
    thumb.src = next.thumbnailUrl || next.mediumUrl || next.heroUrl || next.url || next.originalUrl;
    const full = new Image();
    full.src = next.heroUrl || next.mediumUrl || next.url || next.originalUrl || next.thumbnailUrl;
  }, [next?.id, next?.thumbnailUrl, next?.mediumUrl, next?.heroUrl, next?.url, next?.originalUrl]);

  const showPrevious = () => setIndex((value) => (value - 1 + images.length) % images.length);
  const showNext = () => setIndex((value) => (value + 1) % images.length);

  const onKeyDown = (event) => {
    if (images.length < 2) return;
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      showPrevious();
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      showNext();
    } else if (event.key === " " || event.key === "Spacebar") {
      event.preventDefault();
      setPaused((value) => !value);
    }
  };

  if (loading) return <LeagueGallerySkeleton />;

  if (!images.length) {
    return (
      <div className="league-trophy-card" aria-label="Current League leader">
        <div className="league-trophy-halo" />
        <div className="league-live-badge"><i /> LIVE LEADER</div>
        <div className="league-trophy">♛</div>
        <p>{leader ? "Current leaders" : "League foundation ready"}</p>
        <strong>{leader?.name || `Add your first ${vocabulary.singularLower}`}</strong>
        <span>{leader ? `${leader.points} League Points` : `Configure ${vocabulary.pluralLower} in League Admin`}</span>
      </div>
    );
  }

  return (
    <div
      className={`league-gallery-card${paused ? " is-paused" : ""}`}
      ref={cardRef}
      tabIndex={0}
      onKeyDown={onKeyDown}
      aria-roledescription="carousel"
      aria-label="Matrix League team photo slideshow"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={(event) => {
        if (!cardRef.current?.contains(event.relatedTarget)) setPaused(false);
      }}
    >
      <ProgressiveLeagueImage key={active.id || index} image={active} motionEnabled={autoPlaying} />
      <div className="league-gallery-overlay">
        <div>
          <small>{active.caption || "Matrix League moments"}</small>
          <strong>{leader?.name || "Season underway"}</strong>
          <span>{leader ? `${leader.points} League Points` : "Compete. Improve. Win."}</span>
        </div>
      </div>

      {images.length > 1 ? (
        <>
          <button className="league-gallery-arrow prev" type="button" onClick={showPrevious} aria-label="Previous photo">‹</button>
          <button className="league-gallery-arrow next" type="button" onClick={showNext} aria-label="Next photo">›</button>
          <button
            className="league-gallery-pause"
            type="button"
            onClick={() => setPaused((value) => !value)}
            aria-label={paused ? "Resume slideshow" : "Pause slideshow"}
            aria-pressed={paused}
          >
            {paused ? "▶" : "Ⅱ"}
          </button>
          <div className="league-gallery-dots">
            {images.map((image, imageIndex) => (
              <button
                key={image.id || imageIndex}
                className={imageIndex === index ? "active" : ""}
                onClick={() => setIndex(imageIndex)}
                aria-label={`Show image ${imageIndex + 1}`}
                aria-current={imageIndex === index ? "true" : undefined}
              />
            ))}
          </div>
          <div className="league-gallery-progress" aria-hidden="true">
            <span
              key={`${active.id || index}-${paused}-${autoPlaying}`}
              className={autoPlaying ? "running" : ""}
              style={{ "--league-slide-duration": `${intervalMs}ms` }}
            />
          </div>
        </>
      ) : null}
    </div>
  );
}

function StandingsContent({ standings, season, loading, movement, lastUpdated, completedCompetitions, fixtures }) {
  return (
    <>
      <section id="standings" className="league-panel league-standings-page-panel">
        <div className="league-panel-head">
          <div><p>Live table</p><h2>The race for operational excellence</h2></div>
          <div className="league-panel-actions"><Link to="/league/admin">League admin</Link></div>
        </div>
        <div className="league-division-tables">
          <section className="league-division-table gold">
            <div className="league-division-title"><span>Gold League</span><h3>Gold standings</h3></div>
            <LiveTable standings={standings} season={season} loading={loading} movement={movement} lastUpdated={lastUpdated} division="gold" competitions={completedCompetitions} />
          </section>
          <section className="league-division-table silver">
            <div className="league-division-title"><span>Silver League</span><h3>Silver standings</h3></div>
            <LiveTable standings={standings} season={season} loading={loading} movement={movement} lastUpdated={lastUpdated} division="silver" competitions={completedCompetitions} />
          </section>
        </div>
      </section>
      <PromotionZone standings={standings} season={season} />
      <AwardsPanel standings={standings} fixtures={fixtures} />
    </>
  );
}

export default function LeagueHome({ initialSection = "overview" }) {
  const [competitions, setCompetitions] = useState([]);
  const [eventDetail, setEventDetail] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [galleryInterval, setGalleryInterval] = useState(DEFAULT_GALLERY_SLIDESHOW_INTERVAL);
  const [galleryLoading, setGalleryLoading] = useState(true);
  const [galleryError, setGalleryError] = useState("");

  const { orgId, season, standings, loading, error } = useLeagueData();
  const vocabulary = useLeagueVocabulary(orgId);
  const goldStandings = standings.filter((site) => String(site.division || "gold").toLowerCase() === "gold");
  const silverStandings = standings.filter((site) => String(site.division || "gold").toLowerCase() === "silver");
  const leader = goldStandings[0] || silverStandings[0];
  const { fixtures } = useLeagueFixtures(orgId, season.id, season.currentRound || 1);
  const { movement, lastUpdated } = useStandingsMovement(orgId, season.id, standings);

  useEffect(() => (orgId ? subscribeCompetitions(orgId, setCompetitions, () => {}) : undefined), [orgId]);
  useEffect(() => {
    if (!orgId) return undefined;
    setGalleryLoading(true);
    setGalleryError("");
    return subscribeLeagueGallery(
      orgId,
      (images, settings) => {
        setGallery(images);
        setGalleryInterval(settings?.slideshowIntervalMs || DEFAULT_GALLERY_SLIDESHOW_INTERVAL);
        setGalleryLoading(false);
      },
      (gallerySubscriptionError) => {
        setGalleryError(gallerySubscriptionError?.message || "Unable to load League photos.");
        setGalleryLoading(false);
      },
    );
  }, [orgId]);

  const publishedCompetitions = useMemo(
    () => competitions.filter((item) => item.status === "published" || item.status === "completed"),
    [competitions],
  );
  const completedCompetitions = useMemo(
    () => competitions
      .filter((item) => item.status === "completed")
      .sort((a, b) => (b.completedAt?.seconds || 0) - (a.completedAt?.seconds || 0)),
    [competitions],
  );

  if (initialSection === "standings") {
    return (
      <div className="league-home league-standings-page">
        {error ? <div className="league-alert">{error}</div> : null}
        <StandingsContent
          standings={standings}
          season={season}
          loading={loading}
          movement={movement}
          lastUpdated={lastUpdated}
          completedCompetitions={completedCompetitions}
          fixtures={fixtures}
        />
      </div>
    );
  }

  return (
    <div className="league-home">
      <section className="league-hero league-live-hero">
        <div>
          <p className="league-eyebrow">Matrix League · {season.divisionName}</p>
          <h1>Compete.<br /><span>Improve.</span> Win.</h1>
          <p className="league-hero-copy">Turn operational excellence into a season your teams want to win.</p>
          <div className="league-hero-actions">
            <Link to="/league/table" className="league-primary-button">Open live table</Link>
            <span className="league-status"><i /> {season.status === "live" ? "Season live" : season.status === "complete" || season.status === "archived" ? "Season complete" : "Season draft"}</span>
          </div>
        </div>
        <LeagueGallery images={gallery} leader={leader} vocabulary={vocabulary} intervalMs={galleryInterval} loading={galleryLoading} />
      </section>

      {galleryError ? <div className="league-alert league-gallery-alert">{galleryError}</div> : null}

      <section className="league-kpis">
        <article><span>{vocabulary.plural}</span><strong>{standings.length}</strong><small>Competing {vocabulary.pluralLower}</small></article>
        <article><span>Top score</span><strong>{leader?.points || 0}</strong><small>{leader?.name || `Awaiting ${vocabulary.pluralLower}`}</small></article>
        <article><span>Season</span><strong>{season.status === "live" ? "LIVE" : season.status === "archived" || season.status === "complete" ? "COMPLETE" : "DRAFT"}</strong><small>{season.name}</small><small>{season.startDate || "Start TBC"} → {season.endDate || "End TBC"}</small></article>
      </section>

      {error ? <div className="league-alert">{error}</div> : null}
      <LeagueInsights standings={standings} competitions={publishedCompetitions} season={season} movement={movement} />

      <section className="league-matchday-grid">
        <article className="league-panel">
          <div className="league-panel-head"><div><p>Operations centre</p><h2>Upcoming competitions</h2></div><Link to="/league/events">All events</Link></div>
          <div className="league-home-events">
            {publishedCompetitions.filter((item) => item.status === "published").slice(0, 3).length
              ? publishedCompetitions.filter((item) => item.status === "published").slice(0, 3).map((item) => (
                <button key={item.id} onClick={() => setEventDetail(item)}>
                  <div className="league-home-event-main">
                    <span>{item.visibility === "mystery" ? "Mystery" : item.category || "Competition"}</span>
                    <strong>{item.visibility === "mystery" ? "Mystery competition" : item.name}</strong>
                    <small>{item.startDate || "TBC"} {item.startTime || ""} → {item.endDate || "TBC"} {item.endTime || ""}</small>
                    <em>{(item.places || []).reduce((sum, place) => sum + Number(place.points || 0), 0)} points available{Number(item.participationPoints || 0) > 0 ? ` · ${item.participationPoints} participation` : ""}</em>
                  </div>
                  <CompetitionCountdown item={item} />
                </button>
              ))
              : <div className="league-empty">No published competitions scheduled yet.</div>}
          </div>
        </article>

        <article className="league-panel">
          <div className="league-panel-head"><div><p>Live timeline</p><h2>Recent outcomes</h2></div></div>
          <div className="league-home-outcomes">
            {completedCompetitions.slice(0, 3).length
              ? completedCompetitions.slice(0, 3).map((item) => {
                const ordered = (item.winners || []).slice().sort((a, b) => Number(a.place) - Number(b.place));
                const first = ordered.find((winner) => Number(winner.place) === 1);
                const firstName = standings.find((site) => site.id === first?.siteId)?.name || "Winner not recorded";
                return (
                  <button key={item.id} onClick={() => setEventDetail(item)}>
                    <span>{item.category || "Competition"}</span>
                    <strong>{item.name}</strong>
                    <b className="league-outcome-winner">Winner · {firstName}{first ? ` · ${Number(first.points || 0)} pts` : ""}</b>
                    <small>{ordered.slice(1).map((winner) => `${winner.place}${winner.place === 2 ? "nd" : "rd"}: ${standings.find((site) => site.id === winner.siteId)?.name || "Site"} · ${Number(winner.points || 0)} pts`).join(" · ") || "No additional placements"}</small>
                  </button>
                );
              })
              : <div className="league-empty">Competition outcomes will appear here.</div>}
          </div>
        </article>
      </section>

      <CompetitionDetailModal item={eventDetail} sites={standings} onClose={() => setEventDetail(null)} />
    </div>
  );
}
